import { ReactNode, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useMetaAdVisibilityQuery } from '../../api/client'
import Button from '../../components/Button'
import InfoCallout from '../../components/callout/InfoCallout'
import WarningCallout from '../../components/callout/WarningCallout'
import MastheadBreadcrumb from '../../components/MastheadBreadcrumb'
import SimpleLoader from '../../components/SimpleLoader'
import { TextPageContainer } from '../../containers/TextPageContainer'
import PageMasthead from '../../components/PageMasthead'

type ParamTypes = {
    accountId: string
}

const HOW_IT_WORKS_LS_KEY = 'metaAdVisibility.howItWorks.open'

/**
 * Admin diagnostic for Meta ad-account visibility (EN-210).
 *
 * Audience is Service Delivery, not engineers. The page leads with a brief
 * intro and an expandable "How does this work?" explainer (open by default
 * for first-time visitors, persisted closed in localStorage once dismissed).
 * Then the actual data, then a Technical Details accordion that matches the
 * explainer's style.
 *
 * Each render hits the Marketing API live, so it's slow — expect seconds.
 */
export default function MetaAdVisibilityPage() {
    const { accountId } = useParams<ParamTypes>()
    const parsedAccountId = parseInt(accountId as string)
    const [days, setDays] = useState(30)

    const { data, loading, error, refetch } = useMetaAdVisibilityQuery({
        variables: { accountId: parsedAccountId, days },
        // Always fetch fresh — point of the page is the live snapshot.
        fetchPolicy: 'network-only'
    })

    return (
        <>
            <PageMasthead
                title="Meta ad-account visibility (admin)"
                tabTitle="Meta ad-account visibility (admin)"
                breadcrumb={
                    <MastheadBreadcrumb
                        to={`/account/${accountId}/settings`}
                        label="Profile settings"
                    />
                }
            />
            <TextPageContainer>
                <p className="mt-3 text-sm text-gray-700">
                    Live diagnostic for which Meta ad accounts Arwen can reach for this profile, and
                    whether that visibility is good enough to classify ad comments correctly. Use this
                    when ad-comment counts look off, or before deciding whether a client needs to fix
                    their Meta permissions.
                </p>

                <div className="mt-4 flex items-center gap-2">
                    <label htmlFor="days" className="text-sm">
                        Look-back window:
                    </label>
                    <input
                        id="days"
                        type="number"
                        min={1}
                        max={365}
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value) || 30)}
                        className="w-20 rounded border border-gray-300 px-2 py-1"
                    />
                    <span className="text-sm text-gray-500">days</span>
                    <Button onClick={() => refetch()} text="Refresh" primary />
                </div>

                {loading && (
                    <div className="mt-6">
                        <SimpleLoader loading={true} />
                        <p className="text-sm text-gray-600">
                            Calling the Meta Marketing API — this can take several seconds.
                        </p>
                    </div>
                )}

                {error && (
                    <WarningCallout>
                        <strong>Couldn't load ad visibility:</strong> {error.message}
                    </WarningCallout>
                )}

                {data?.metaAdVisibility && <Report report={data.metaAdVisibility} />}
            </TextPageContainer>
        </>
    )
}

type ReportType = NonNullable<
    ReturnType<typeof useMetaAdVisibilityQuery>['data']
>['metaAdVisibility']

type HealthStatus = 'healthy' | 'incomplete' | 'blind' | 'unknown' | 'info-only'

/**
 * Facebook and Instagram have different stakes here:
 *
 * - On **Facebook**, ad-comment classification depends on us having a Post
 *   record for the ad. If the authoriser can't see the ad account that ran
 *   the ad, we don't sync the ad, and comments on it land as organic (or
 *   unmatched). That's a correctness issue — for ad-supported clients it's
 *   potentially serious.
 *
 * - On **Instagram**, every webhook comment event carries `media.ad_id` or
 *   `media.original_media_id`. We always know whether a comment is on an
 *   ad. Ad-account visibility is then about *completeness of metadata*,
 *   not correctness of classification. Never urgent on IG.
 */
function classifyHealth(report: ReportType): HealthStatus {
    const isInstagram = report.socialMediaService.toLowerCase() === 'instagram'

    if (isInstagram) {
        // On IG, this page is always informational — even with zero visible
        // ad accounts, comment classification still works via webhook fields.
        return 'info-only'
    }

    // Facebook from here on.
    if (report.totalCount === 0) return 'blind'
    if (report.matchedCount === 0) return 'incomplete'
    if (report.totalComments === 0) return 'unknown' // not enough signal
    const pct = (report.unmatchedComments / report.totalComments) * 100
    if (pct >= 20) return 'incomplete'
    return 'healthy'
}

function Report({ report }: { report: ReportType }) {
    const status = classifyHealth(report)
    const isInstagram = report.socialMediaService.toLowerCase() === 'instagram'
    const what = isInstagram ? 'Instagram account' : 'Facebook page'
    const pct =
        report.totalComments > 0 ? (report.unmatchedComments / report.totalComments) * 100 : 0

    return (
        <div className="mt-6 flex flex-col gap-8">
            <HowItWorks isInstagram={isInstagram} />
            <Headline
                report={report}
                status={status}
                what={what}
                pct={pct}
                isInstagram={isInstagram}
            />
            <AuthoriserAndToken authoriser={report.authoriser ?? null} />
            <VisibleAdAccounts report={report} what={what} isInstagram={isInstagram} />
            {!isInstagram && <UnmatchedComments report={report} pct={pct} />}
            <TechnicalDetails report={report} />
        </div>
    )
}

// ─── Reusable accordion ────────────────────────────────────────────────────

/**
 * A consistent accordion shell used for both "How does this work?" and
 * "Technical details". Optionally persists open/closed state to localStorage.
 *
 * Native `<details>` doesn't easily integrate with persisted state, so we
 * track `open` explicitly and use `onToggle` to write through.
 */
function Accordion({
    summary,
    children,
    storageKey,
    defaultOpen = false
}: {
    summary: ReactNode
    children: ReactNode
    storageKey?: string
    defaultOpen?: boolean
}) {
    const [open, setOpen] = useState<boolean>(() => {
        if (!storageKey || typeof window === 'undefined') return defaultOpen
        const stored = window.localStorage.getItem(storageKey)
        if (stored === null) return defaultOpen
        return stored === 'true'
    })

    useEffect(() => {
        if (!storageKey || typeof window === 'undefined') return
        window.localStorage.setItem(storageKey, String(open))
    }, [open, storageKey])

    return (
        <section className="rounded-md border border-gray-200 bg-gray-50">
            <details open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
                <summary className="cursor-pointer px-5 py-3 text-base font-semibold text-gray-800">
                    {summary}
                </summary>
                <div className="border-t border-gray-200 px-5 py-4">{children}</div>
            </details>
        </section>
    )
}

// ─── How it works (open by default, persists closed) ─────────────────────

function HowItWorks({ isInstagram }: { isInstagram: boolean }) {
    const what = isInstagram ? 'Instagram account' : 'Facebook page'
    return (
        <Accordion
            summary="How does this work? Why might we be missing ad accounts?"
            storageKey={HOW_IT_WORKS_LS_KEY}
            defaultOpen={true}
        >
            <p className="mt-0 text-sm">
                A {what} can be advertised on by ad accounts from <strong>any</strong> Meta Business
                Manager — including third-party ones such as media agencies. What determines whether
                Arwen can see an ad account is{' '}
                <em>whether the authorising user has a role on it</em>, not which Business Manager
                it lives in. We can see ad accounts in third-party BMs as long as the authoriser is
                invited as a user. We <em>can't</em> see ad accounts even in the client's own BM if
                the authoriser doesn't have a role on them.
            </p>
            {!isInstagram && (
                <p className="mt-2 text-sm">
                    On Facebook, this matters for <strong>correctness</strong>: if an ad account is
                    invisible to us, the ads it runs don't get a Post record, so comments on those
                    ads can't be matched and land as organic. For ad-supported clients, that's
                    potentially serious.
                </p>
            )}
            {isInstagram && (
                <p className="mt-2 text-sm">
                    On Instagram, every comment webhook tells us directly whether it's on an ad — so
                    ad-vs-organic classification doesn't depend on this page. Visibility here is
                    about <strong>completeness of metadata</strong> (ad names, campaign IDs, etc.),
                    not about correctness.
                </p>
            )}

            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-green-300 bg-white p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-green-700">
                        ✓ Visible to Arwen
                    </div>
                    <p className="mt-0 text-sm">
                        Any ad account where the authorising user has a role — regardless of which
                        Business Manager it lives in.
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-gray-700">
                        <li>• In client's BM, authoriser has access</li>
                        <li>• In agency's BM, authoriser invited as user</li>
                    </ul>
                </div>

                <div className="rounded-md border border-red-300 bg-white p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">
                        ✗ Invisible to Arwen
                    </div>
                    <p className="mt-0 text-sm">
                        Any ad account where the authorising user has <em>no</em> role. The ads
                        still run on the {what}, comments still arrive via webhook, but we have no
                        record of the underlying ad.
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-gray-700">
                        <li>• Agency BM, authoriser not added</li>
                        <li>• Internal client BM, authoriser excluded</li>
                    </ul>
                </div>
            </div>

            <p className="mt-4 text-xs italic text-gray-500">
                This is a teaching diagram. The "Visible ad accounts" table below shows this
                specific client's actual setup.
            </p>
        </Accordion>
    )
}

// ─── Headline ───────────────────────────────────────────────────────────────

/** Plain-English copy for the "what should we do" line, by status. */
function fix(status: HealthStatus, isInstagram: boolean): string | null {
    if (isInstagram) return null
    switch (status) {
        case 'blind':
            return 'Fix: ask the client to make sure the authorising user has a role on the ad accounts running ads on this page in Meta Business Manager, then re-authorise Arwen. Re-auth alone is not enough if the underlying access is missing.'
        case 'incomplete':
            return 'Fix: the authorising user has Business Manager access but not to the ad accounts running ads on this page. Have the client add the authoriser to those ad accounts in Business Manager, then re-authorise.'
        default:
            return null
    }
}

function Headline({
    report,
    status,
    what,
    pct,
    isInstagram
}: {
    report: ReportType
    status: HealthStatus
    what: string
    pct: number
    isInstagram: boolean
}) {
    const { badge, headline, explainer } = (() => {
        switch (status) {
            case 'healthy':
                return {
                    badge: { label: 'Healthy', cls: 'bg-green-100 text-green-800' },
                    headline: `We can see ${report.matchedCount} of ${report.totalCount} ad accounts attached to this ${what}.`,
                    explainer: `Recent unmatched-comment ratio is ${pct.toFixed(1)}% — within normal bounds.`
                }
            case 'incomplete':
                return {
                    badge: {
                        label: 'Possibly incomplete',
                        cls: 'bg-yellow-100 text-yellow-800'
                    },
                    headline:
                        report.matchedCount === 0
                            ? `Our token sees ${report.totalCount} ad account(s), but none of them list this ${what}.`
                            : `${pct.toFixed(1)}% of recent comments on this ${what} can't be matched to a known post.`,
                    explainer:
                        report.matchedCount === 0
                            ? `Ads on this ${what} are likely being run from ad accounts the authorising user doesn't have a role on — typically a media agency the authoriser isn't a user of.`
                            : `That's above our 20% warning threshold. Strongly suggests we're missing ad-account access.`
                }
            case 'blind':
                return {
                    badge: { label: 'Blind', cls: 'bg-red-100 text-red-800' },
                    headline: `Our token sees zero ad accounts.`,
                    explainer: `The authorising user has no Business Manager role on any ad accounts, or scopes are missing on the connection.`
                }
            case 'unknown':
                return {
                    badge: { label: 'Not enough data', cls: 'bg-gray-100 text-gray-800' },
                    headline: `We can see ${report.matchedCount} of ${report.totalCount} ad accounts attached to this ${what}.`,
                    explainer: `No comments in the look-back window — nothing to assess the unmatched ratio against. Widen the window or wait for traffic.`
                }
            case 'info-only': {
                // Instagram. Always blue/informational, never urgent.
                const matchSummary =
                    report.totalCount === 0
                        ? `Our token sees zero ad accounts attached via the authorising user.`
                        : `We can see ${report.matchedCount} of ${report.totalCount} ad accounts attached to this Instagram account.`
                return {
                    badge: {
                        label: 'Informational',
                        cls: 'bg-blue-100 text-blue-800'
                    },
                    headline: matchSummary,
                    explainer: `On Instagram, ad-vs-organic classification doesn't depend on this — webhook events tell us directly whether each comment is on an ad. This page is informational only; it's not urgent even when nothing's visible.`
                }
            }
        }
    })()

    const fixLine = fix(status, isInstagram)

    return (
        <section className="rounded-md border border-gray-200 bg-gray-50 p-5">
            <div className="mb-2 flex items-center gap-3">
                <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${badge.cls}`}
                >
                    {badge.label}
                </span>
                <span className="text-sm text-gray-500">
                    {report.accountName ?? '(no name)'} — {report.socialMediaService}
                </span>
            </div>
            <p className="m-0 text-base font-medium">{headline}</p>
            <p className="m-0 mt-1 text-sm text-gray-700">{explainer}</p>
            {fixLine && (
                <p className="m-0 mt-3 rounded border border-gray-300 bg-white p-3 text-sm text-gray-800">
                    {fixLine}
                </p>
            )}
        </section>
    )
}

// ─── Authoriser & token ───────────────────────────────────────────────────

type AuthoriserType = NonNullable<ReportType['authoriser']>

/**
 * Identity of the user who connected this profile + current token state.
 * Pulled live from Meta (`/me` + `/debug_token`). Best-effort — if either
 * Graph API call failed the backend returns `null` here and we tell the
 * user.
 */
function AuthoriserAndToken({ authoriser }: { authoriser: AuthoriserType | null }) {
    if (!authoriser) {
        return (
            <section>
                <h2 className="text-lg font-semibold">Authoriser &amp; token</h2>
                <InfoCallout>
                    Couldn't fetch the authoriser's identity or token details from Meta. The
                    ad-visibility data above is still trustworthy — but we don't know who
                    authorised, or whether the token is currently valid.
                </InfoCallout>
            </section>
        )
    }

    // Format token expiry. expires_at = 0 / null typically means "never expires".
    const expiry = authoriser.tokenExpiresAt
        ? new Date(authoriser.tokenExpiresAt).toLocaleString()
        : 'No fixed expiry'
    const dataExpiry = authoriser.tokenDataAccessExpiresAt
        ? new Date(authoriser.tokenDataAccessExpiresAt).toLocaleString()
        : '(unknown)'

    return (
        <section>
            <h2 className="text-lg font-semibold">Authoriser &amp; token</h2>
            <p className="mt-1 text-sm text-gray-700">
                Who connected this profile to Arwen, and the current state of their token. Pulled
                live from Meta. If permissions need fixing, this is the human you (or the client)
                needs to talk to.
            </p>

            <div className="mt-3 grid gap-4 md:grid-cols-2">
                {/* Authoriser identity */}
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Authoriser
                    </div>
                    <div className="text-base font-medium">
                        {authoriser.name ?? '(name not available)'}
                    </div>
                    {authoriser.email && (
                        <div className="text-sm text-gray-700">{authoriser.email}</div>
                    )}
                    <div className="mt-1 font-mono text-xs text-gray-500">
                        FB user ID: {authoriser.userId}
                    </div>
                    {!authoriser.name && (
                        <p className="mt-2 text-xs italic text-gray-500">
                            Meta didn't return a name for this token. Often this means the
                            authoriser hasn't granted the basic profile permission — but the
                            connection still works.
                        </p>
                    )}
                </div>

                {/* Token state */}
                <div
                    className={`rounded-md border p-4 ${
                        authoriser.tokenIsValid
                            ? 'border-green-300 bg-white'
                            : 'border-red-300 bg-red-50'
                    }`}
                >
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Token
                    </div>
                    <div className="flex items-center gap-2">
                        {authoriser.tokenIsValid ? (
                            <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                                ✓ Valid
                            </span>
                        ) : (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                                ✗ Invalid
                            </span>
                        )}
                        <span className="text-sm text-gray-700">
                            via {authoriser.applicationName}
                        </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                        <dt className="text-gray-500">Token expires</dt>
                        <dd>{expiry}</dd>
                        <dt className="text-gray-500">Data-access expires</dt>
                        <dd>{dataExpiry}</dd>
                    </dl>
                </div>
            </div>

            {/* Scopes */}
            <div className="mt-3 rounded-md border border-gray-200 bg-white p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Granted scopes ({authoriser.scopes.length})
                </div>
                {authoriser.scopes.length === 0 ? (
                    <p className="m-0 text-sm italic text-gray-500">
                        No scopes returned. The token is essentially useless in this state.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {authoriser.scopes.map((scope) => (
                            <span
                                key={scope}
                                className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700"
                            >
                                {scope}
                            </span>
                        ))}
                    </div>
                )}
                <p className="mt-3 text-xs text-gray-500">
                    Scopes that matter for ad-account visibility:{' '}
                    <code className="rounded bg-gray-100 px-1 text-[10px]">ads_read</code>,{' '}
                    <code className="rounded bg-gray-100 px-1 text-[10px]">ads_management</code>,{' '}
                    <code className="rounded bg-gray-100 px-1 text-[10px]">pages_show_list</code>,{' '}
                    <code className="rounded bg-gray-100 px-1 text-[10px]">
                        business_management
                    </code>
                    . Missing any of these usually explains a "Blind" status above.
                </p>
            </div>
        </section>
    )
}

// ─── Visible ad accounts ──────────────────────────────────────────────────

function VisibleAdAccounts({
    report,
    what,
    isInstagram
}: {
    report: ReportType
    what: string
    isInstagram: boolean
}) {
    if (report.totalCount === 0) {
        return (
            <section>
                <h2 className="text-lg font-semibold">Visible ad accounts</h2>
                {isInstagram ? (
                    <InfoCallout>
                        Our token sees zero ad accounts via the authorising user. On Instagram this
                        isn't a correctness problem — comment classification works regardless. It
                        just means we can't fetch ad metadata (ad names, campaign IDs) for this
                        account.
                    </InfoCallout>
                ) : (
                    <WarningCallout>
                        Our token sees zero ad accounts. The authorising user has no Business
                        Manager role on any ad accounts, or the connection is missing scopes. To
                        fix: make sure the authorising user has access to the right ad accounts in
                        Business Manager, then have the client re-authorise.
                    </WarningCallout>
                )}
            </section>
        )
    }

    return (
        <section>
            <h2 className="text-lg font-semibold">
                Visible ad accounts ({report.matchedCount} attached to this {what} /{' '}
                {report.totalCount} total)
            </h2>
            <p className="mt-1 text-sm text-gray-700">
                Every ad account the authorising user has a role on — wherever those ad accounts
                live in Meta. "Attached" means the ad account lists this {what} as a place it can
                advertise, so we'll sync its ads via the Marketing API. Unattached ones still appear
                here because the authorising user has access for other reasons (e.g. they manage
                other brands), but they don't matter for this profile.
                {isInstagram && (
                    <>
                        {' '}
                        On Instagram this list is mainly <strong>informational</strong> — comment
                        classification works regardless of what's here.
                    </>
                )}
            </p>
            <table className="mt-3 w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b border-gray-200 text-left">
                        <th className="py-2 pr-4">Attached?</th>
                        <th className="py-2 pr-4">Ad account</th>
                        <th className="py-2 pr-4">Pages / handles it can advertise on</th>
                    </tr>
                </thead>
                <tbody>
                    {report.adAccounts.map((ad) => (
                        <tr key={ad.id} className="border-b border-gray-100 align-top">
                            <td className="py-2 pr-4">
                                {ad.isMatch ? (
                                    <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                                        ✓ Attached
                                    </span>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </td>
                            <td className="py-2 pr-4">
                                <div className="font-medium">{ad.name}</div>
                                <div className="font-mono text-xs text-gray-500">{ad.id}</div>
                            </td>
                            <td className="py-2 pr-4">
                                {ad.attachments.length === 0 ? (
                                    <span className="text-gray-400">(none)</span>
                                ) : (
                                    <ul className="m-0 list-none p-0">
                                        {ad.attachments.map((a) => (
                                            <li key={a.id} className="text-xs">
                                                {a.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    )
}

// ─── Unmatched comments (FB only) ────────────────────────────────────────

function UnmatchedComments({ report, pct }: { report: ReportType; pct: number }) {
    return (
        <section>
            <h2 className="text-lg font-semibold">
                Unmatched comments — last {report.daysWindow} days
            </h2>
            <p className="mt-1 text-sm text-gray-700">
                The percentage of recent Facebook comments that arrived without Arwen being able to
                identify the parent post. This is the most useful single signal that ad-account
                visibility is incomplete — it can catch problems the table above misses
                (specifically, ad accounts the authorising user has no access to at all).
            </p>
            <p className="mt-2 text-xs text-gray-600">
                The look-back window above controls how far back we count. Default is 30 days. Widen
                it (90, 180) to confirm a sustained issue rather than a one-off; narrow it (7, 14)
                when investigating a specific recent incident. Low-traffic accounts may need a wider
                window to have enough signal.
            </p>
            {report.totalComments === 0 ? (
                <InfoCallout>
                    No Facebook comments in this window — nothing to judge. Widen the look-back
                    window or wait for traffic.
                </InfoCallout>
            ) : (
                <>
                    <p className="mt-2">
                        <strong>
                            {report.unmatchedComments.toLocaleString()} of{' '}
                            {report.totalComments.toLocaleString()}
                        </strong>{' '}
                        comments couldn't be matched to a known post ({pct.toFixed(1)}%).
                    </p>
                    {pct >= 20 ? (
                        <WarningCallout>
                            Above 20% — strong signal that ad-account visibility is incomplete. The
                            matched-account table above will usually show why; if it looks fine, the
                            missing ad account is one our token can't see at all.
                        </WarningCallout>
                    ) : (
                        <InfoCallout>
                            A healthy account sits near 0%. Persistent values above 20% are our
                            alerting threshold.
                        </InfoCallout>
                    )}
                </>
            )}
        </section>
    )
}

// ─── Technical details (matched style to How it works) ───────────────────

function TechnicalDetails({ report }: { report: ReportType }) {
    return (
        <Accordion summary="Technical details">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-gray-500">Arwen account ID</dt>
                <dd className="font-mono">{report.accountId}</dd>
                <dt className="text-gray-500">{report.identityLabel}</dt>
                <dd className="font-mono">{report.serviceAccountId}</dd>
                {report.serviceAccountUsername && (
                    <>
                        <dt className="text-gray-500">Username</dt>
                        <dd>{report.serviceAccountUsername}</dd>
                    </>
                )}
                <dt className="text-gray-500">Meta API attachment field</dt>
                <dd className="font-mono">{report.attachmentLabel}</dd>
                <dt className="text-gray-500">Last successful ad fetch</dt>
                <dd>
                    {report.adsLastFetched
                        ? new Date(report.adsLastFetched).toLocaleString()
                        : '(never)'}
                </dd>
            </dl>
            <p className="mt-3 text-xs text-gray-500">
                Raw ad-account IDs are shown under each ad account in the table above. See{' '}
                <a
                    href="https://arwen-ai.atlassian.net/browse/EN-210"
                    target="_blank"
                    rel="noreferrer"
                >
                    EN-210
                </a>{' '}
                and the{' '}
                <a
                    href="https://www.notion.so/3664f06b4bd1819b88e6c3a7bd3e0e37"
                    target="_blank"
                    rel="noreferrer"
                >
                    Notion explainer
                </a>{' '}
                for the full mental model.
            </p>
        </Accordion>
    )
}
